import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {DxDateRangeBoxComponent, DxSelectBoxComponent} from 'devextreme-angular';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    BlockedStockScoreAggregateDto,
    BlockedStockScoreAggregateQuery,
    InventoryClient,
    ThemeService
} from '@services';
import {BlockedStockChartComponent, BlockedStockSeriesConfig} from './blocked-stock-chart/blocked-stock-chart.component';
import {
    BlockedStockDoughnutChartComponent,
    DoughnutSegmentConfig
} from './blocked-stock-doughnut-chart/blocked-stock-doughnut-chart.component';
import {ChartsComponent} from "../../../common/charts.component";

const themeColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();

export function blockedStockSeries(): BlockedStockSeriesConfig[] {
    return [
        {valueField: 'blockedStock', name: 'Blocked Stock', color: themeColor('--bs-info')},
        {valueField: 'unmatchedBlockedStock', name: 'Unmatched Blocked Stock', color: themeColor('--bs-danger')}
    ];
}

export function daysOnHoldSegments(): DoughnutSegmentConfig[] {
    return [
        {argument: '30 Days on Hold', valueField: 'onHold30Days', color: themeColor('--bs-success')},
        {argument: '45 Days on Hold', valueField: 'onHold45Days', color: themeColor('--bs-warning')},
        {argument: '90 Days on Hold', valueField: 'onHold90Days', color: themeColor('--bs-danger')}
    ];
}

export function reasonSegments(): DoughnutSegmentConfig[] {
    return [
        {argument: 'Expired', valueField: 'expired', color: themeColor('--bs-danger')},
        {
            argument: 'Quality Issue',
            combineFields: ['foreignMaterial', 'labellingPackaging', 'infestation'],
            color: themeColor('--bs-blue')
        },
        {argument: 'Returns', valueField: 'returns', color: themeColor('--bs-warning')},
        {argument: 'Damage', valueField: 'damage', color: themeColor('--bs-gray-600')},
        {argument: 'Logistical', valueField: 'logistical', color: themeColor('--bs-success')}
    ];
}

@Component({
    selector: 'app-blocked-stock-charts',
    imports: [
        DxDateRangeBoxComponent,
        DxSelectBoxComponent,
        BlockedStockChartComponent,
        BlockedStockDoughnutChartComponent
    ],
    templateUrl: './blocked-stock-charts.component.html'
})
export class BlockedStockChartsComponent extends ChartsComponent implements OnInit {
    private inventoryClient = inject(InventoryClient);
    private destroyRef = inject(DestroyRef);
    private themeService = inject(ThemeService);

    title = 'My Score';

    data: BlockedStockScoreAggregateDto[] | null = null;

    blockedStockSeries: BlockedStockSeriesConfig[] = [];
    blockedStockPercentSeries: BlockedStockSeriesConfig[] = [];
    noRacCodingSeries: BlockedStockSeriesConfig[] = [];
    onHold30DaysSeries: BlockedStockSeriesConfig[] = [];
    onHold45DaysSeries: BlockedStockSeriesConfig[] = [];
    onHold90DaysSeries: BlockedStockSeriesConfig[] = [];
    expirySeries: BlockedStockSeriesConfig[] = [];
    qualityIssueSeries: BlockedStockSeriesConfig[] = [];
    returnsSeries: BlockedStockSeriesConfig[] = [];
    damageSeries: BlockedStockSeriesConfig[] = [];
    logisticalSeries: BlockedStockSeriesConfig[] = [];

    daysOnHoldSegments: DoughnutSegmentConfig[] = [];
    reasonSegments: DoughnutSegmentConfig[] = [];

    ngOnInit(): void {
        this.themeService.isDark$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.initSeriesColors());

        this.loadData();
    }

    private initSeriesColors(): void {
        const blue = themeColor('--bs-blue');
        const primary = themeColor('--bs-primary');
        const info = themeColor('--bs-info');
        const warning = themeColor('--bs-warning');
        const danger = themeColor('--bs-danger');
        const red = themeColor('--dx-color-danger');
        const success = themeColor('--bs-success');
        const gray = themeColor('--bs-gray-600');

        this.blockedStockSeries = blockedStockSeries();
        this.blockedStockPercentSeries = [
            {valueField: 'blockedStockPercent', name: '% of Block Stock', color: info}
        ];
        this.noRacCodingSeries = [
            {valueField: 'noRacCodingPercent', name: 'Blocked Stock', color: info},
            {valueField: 'noRacCodingUnmatchedPercent', name: 'Unmatched Blocked Stock', color: danger}
        ];
        this.onHold30DaysSeries = [
            {valueField: 'onHold30DaysPercent', name: '30 Days on Hold', color: success}
        ];
        this.onHold45DaysSeries = [
            {valueField: 'onHold45DaysPercent', name: '45 Days on Hold', color: warning}
        ];
        this.onHold90DaysSeries = [
            {valueField: 'onHold90DaysPercent', name: '90 Days on Hold', color: danger}
        ];
        this.expirySeries = [
            {valueField: 'agedOrNearingExpiryPercent', name: 'Nearing Expiry', color: red},
            {valueField: 'expiredPercent', name: 'Expired', color: danger}
        ];
        this.qualityIssueSeries = [
            {valueField: 'foreignMaterialPercent', name: 'Foreign Material', color: blue},
            {valueField: 'labellingPackagingPercent', name: 'Labelling/Packaging', color: primary},
            {valueField: 'infestationPercent', name: 'Infestation', color: info}
        ];
        this.returnsSeries = [
            {valueField: 'returnsPercent', name: 'Returns', color: warning}
        ];
        this.damageSeries = [
            {valueField: 'damagePercent', name: 'Damage', color: gray}
        ];
        this.logisticalSeries = [
            {valueField: 'logisticalPercent', name: 'Logistical', color: success}
        ];

        this.daysOnHoldSegments = daysOnHoldSegments();
        this.reasonSegments = reasonSegments();
    }

    onDateRangeChanged(e: any): void {
        if (e.value && e.value[0]) {
            this.dateRangeFilterValue = e.value;
            this.loadData();
        }
    }

    onAggregateChanged(): void {
        this.loadData();
    }

    private loadData(): void {
        const query = new BlockedStockScoreAggregateQuery({
            aggregateType: this.aggregateValue,
            from: this.dateRangeFilterValue[0],
            to: this.dateRangeFilterValue[1],
            timeZoneOffset: (new Date()).getTimezoneOffset()
        });

        this.inventoryClient.getBlockedStockScoreAggregate(query)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(result => {
                this.data = result || [];
                this.updateRanges();
            });
    }
}
